<template>
  <div>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Saving...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-alert :model-value="successAlert()" type="success" transition="scale-transition">{{ successMessage }}</v-alert>
    <v-alert :model-value="alert()" type="error" transition="scale-transition">{{ errorObject }}</v-alert>
    <v-form ref="form" v-model="valid" @submit.prevent="" autocomplete="off">
      <div v-if="gettingCommunity" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-if="!gettingCommunity">
        <v-container fluid style="padding-bottom: 0px;">
          <span class="screenHeading">{{ edit ? 'Edit' : 'Create a new' }} community</span>
        </v-container>
        <v-container fluid style="padding-top: 0px">
          <v-row>
            <v-col cols="12" sm="6">
              <v-textarea
                required
                v-model="community.communityName"
                label="Name"
                multi-line
                auto-grow
                rows="1"
                :min="minimumTitleLength"
                :max="maximumTitleLength"
                :maxlength="maximumTitleLength"
                :rules="titleRules"
                :readonly="readonly"
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                :readonly="edit || readonly"
                required
                v-model="community.communityAbbreviation"
                label="Short Name"
                auto-grow
                rows="1"
                :min="minimumShortNameLength"
                :max="maximumShortNameLength"
                :rules="shortNameRules"
                maxlength="3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label>Inactive</label>
              <v-switch color="black" :readonly="readonly" v-model="community.inactive" :label="community.inactive ? 'Yes' : 'No'"></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <!-- <v-col cols="12" sm="4">
              <label>Allow Temporary</label>
              <v-switch
                @watch="toggleTemporary"
                v-model="community.changeTypes[0].enabled"
                :label="community.changeTypes[0].enabled ? 'On' : 'Off'"
              ></v-switch>
            </v-col> -->
            <v-col cols="12" sm="4" v-if="isITSupportOrProcessManager">
              <label>Hide this community from all users except Process Manager and IT support</label>
              <v-switch color="black" v-model="community.testCommunity" :label="community.testCommunity ? 'Yes' : 'No'"></v-switch>
            </v-col>
            <v-col cols="12" sm="4" v-if="showConfidentialCheck || isITSupportOrProcessManager">
              <label>Mark this community as confidential</label>
              <v-switch
                color="black"
                v-model="community.isConfidential"
                :label="community.isConfidential ? 'Yes' : 'No'"
                @change="onConfidentialityChange"
                :readonly="readonly"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="4">
              <label>Notifications will be sent from this community</label>
              <v-switch
                color="black"
                :readonly="readonly"
                v-model="community.allowNotifications"
                :label="community.allowNotifications ? 'Yes' : 'No'"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="4" v-if="isITSupportOrProcessManager">
              <label>Override this community notifications</label>
              <v-row dense>
                <v-col cols="6" md="4">
                  <v-switch
                    color="black"
                    v-model="community.overrideNotifications"
                    @change="changeOverrideNotifications"
                    :label="community.overrideNotifications ? 'Yes' : 'No'"
                  ></v-switch>
                </v-col>
                <v-col cols="12" sm="12" md="8" v-if="community.overrideNotifications">
                  <v-text-field
                    label="Send all notifications to:"
                    maxlength="60"
                    :rules="overrideNotificationsToRules"
                    v-model="community.overrideNotificationsTo"
                  >
                    <template v-slot:append>
                      <v-tooltip location="top" text="Set my email">
                        <template v-slot:activator="{ props }">
                          <v-icon @click="setUserOverrideNotificationsTo" v-bind="props" >mdi-autorenew</v-icon>
                        </template>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" sm="4">
              <label>Disable "Enter a new MOC"</label>
              <v-switch
                color="black"
                :readonly="readonly"
                v-model="community.newDisabled"
                :label="community.newDisabled ? 'Yes' : 'No'"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="4">
              <label>Enable "Upload to OpenText"</label>
              <v-switch
                color="black"
                :readonly="readonly"
                v-model="community.openTextEnabled"
                :label="community.openTextEnabled ? 'Yes' : 'No'"
              ></v-switch>
            </v-col>
             <v-col cols="12" sm="4">
              <label>MOC Duration in Days</label>
              <v-text-field v-model="community.durationInDays" type="number" :rules="durationInDaysRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row dense v-if="community.isConfidential">
            <v-col cols="12">
              <people-picker-ms
                label="Authorized people"
                v-model="community.authorizedPeople"
                title="Authorized people"
                :readonly="readonly"
                :confidentialOptions="authorizedPeoplePickerConfidentialOptions"
                @update="args => this.handleUpdateAuthorizedPeople(args)"
                v-bind="$attrs"
                @beforeDelete="handleRemoveAuthorizedPeople"
              ></people-picker-ms>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <people-picker-ms
                label="Community Owners"
                v-model="community.owners"
                title="Community Owners"
                :readonly="readonly"
                @update="args => this.handleUpdateOwners(args)"
              ></people-picker-ms>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <people-picker-ms
                label="Lead Facilitators"
                v-model="community.leadFacilitators"
                title="Lead Facilitators"
                :readonly="readonly"
                @update="args => this.handleUpdateLeadFacilitators(args)"
              ></people-picker-ms>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card class="pa-5">
                <label class="screenHeading">Closers</label>
                <v-select
                  v-model="community.closers"
                  :items="closerRoles"
                  item-title="name"
                  item-value="value"
                  :readonly="readonly"
                  required
                  :error-messages="validateClosers()"
                  :menu-props="{ maxHeight: '400' }"
                  label="Select"
                  multiple
                ></v-select>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Change Types ({{ community.changeTypes.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showChangeTypeSection = !showChangeTypeSection" small>{{
                showChangeTypeSection ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="showChangeTypeSection">
                <!-- <a class="blue-link" @click="addChangeType">Add Change Type</a> -->
                <v-data-table :headers="changeTypeHeaders" :items="community.changeTypes" style="border: solid 1px;">
                  <template v-slot:item="props">
                    <tr>
                      <td>
                        <a type="button" class="blue-link" @click="editChangeType(props.item)">{{ props.item.type.value }}</a>
                      </td>
                      <td>{{ props.item.default_ ? 'Yes' : 'No' }}</td>
                      <td>{{ props.item.enabled ? 'Yes' : 'No' }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">MOC Types ({{ community.mocTypes.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showMocTypes = !showMocTypes" small>{{ showMocTypes ? 'Hide' : 'Show' }}</v-btn>

              <div v-if="showMocTypes">
                <v-row dense v-if="!readonly">
                  <v-col>
                    <a class="blue-link" @click="addType()">Add MOC Type</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit">
                    <a class="blue-link" @click="bulkAddConfiguration('mocType')">Bulk Add MOC Types</a>
                  </v-col>
                </v-row>
                <v-data-table :headers="mocTypesHeaders" :items="community.mocTypes" style="border: solid 1px;">
                  <template v-slot:item="props">
                    <tr>
                      <td>
                        <a class="blue-link" @click="editType(props.item)">{{ props.item.value }}</a>
                      </td>
                      <td>{{ props.item.enabled ? 'Yes' : 'No' }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Change Categories ({{ community.changeCategories.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showChangeCategories = !showChangeCategories" small>{{
                showChangeCategories ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="showChangeCategories">
                <v-row dense v-if="!readonly">
                  <v-col>
                    <a class="blue-link" @click="addChangeCategory()">Add Change Category</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit">
                    <a class="blue-link" @click="bulkAddConfiguration('changeCategory')">Bulk Add Change Categories</a>
                  </v-col>
                </v-row>
                <ChangeCategoryTable
                  :changeCategories="community.changeCategories"
                  @edit="editChangeCategory"
                  @openFAT="openFATDialog"
                  :readonly="readonly"
                >
                </ChangeCategoryTable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Approval Process Types ({{ approvalProcessTypes.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showApprovalProcessTypes = !showApprovalProcessTypes" small>{{
                showApprovalProcessTypes ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="showApprovalProcessTypes">
                <v-row>
                  <v-col>
                    <a class="blue-link" @click="addApprovalProcessType()">Add Approval Process Type</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit && community.changeCategories.length">
                    <a class="blue-link" @click="bulkAddConfiguration('approvalProcessType')">Bulk Add Approval Process Types</a
                    ><br />
                    <a class="blue-link" v-if="approvalProcessTypes.length" @click="bulkAddConfiguration('approvalProcessTypeFA')"
                      >Bulk Add Approvers/Fyis</a
                    >
                  </v-col>
                </v-row>
                <ApprovalProcessTypeTable
                  @edit="editApprovalProcessType"
                  :approvalProcessTypes="approvalProcessTypes"
                  :changeCategories="community.changeCategories"
                  @save="handleSaveApprovalProcessType"
                  @delete="handleDeleteApprovalProcessType"
                  @openFAT="openFATDialog"
                >
                </ApprovalProcessTypeTable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Change Category MOC Types ({{ mocTypes.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showChangeCategoryMOCType = !showChangeCategoryMOCType" small>{{
                showChangeCategoryMOCType ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="showChangeCategoryMOCType">
                <v-row v-if="!readonly">
                  <v-col>
                    <a class="blue-link" @click="addChangeCategoryMOCType()">Add Change Category MOC Type</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col
                    class="text-right"
                    v-if="edit && (community.changeCategories || []).length && (community.mocTypes || []).length"
                  >
                    <a class="blue-link" @click="bulkAddConfiguration('changeCategoryMocType')"
                      >Bulk Add Change Category MOC Types</a
                    >
                  </v-col>
                </v-row>
                <ChangeCategoryMocTypeTable
                  @edit="editChangeCategoryMOCType"
                  :changeCategoryMocTypes="mocTypes"
                  :mocTypes="community.mocTypes"
                  :changeCategories="community.changeCategories"
                  @openFAT="openFATDialog"
                  :readonly="readonly"
                  @delete="handleDeleteChangeCategoryMocType"
                >
                </ChangeCategoryMocTypeTable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Tasks ({{ community.tasks ? community.tasks.length : 0 }})</span>
              <v-btn class="ms-2 btn-show" @click="showTasks = !showTasks" small>{{ showTasks ? 'Hide' : 'Show' }}</v-btn>

              <div v-if="showTasks">
                <v-row v-if="!readonly">
                  <v-col>
                    <a class="blue-link" @click="addTask()">Add Task</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit">
                    <a class="blue-link" @click="bulkAddConfiguration('tasks')">Bulk Add Tasks</a>
                  </v-col>
                </v-row>
                <TaskTable
                  :edit="edit"
                  @edit="editTask"
                  :tasks="community.tasks"
                  :communityAbbreviation="community.communityAbbreviation"
                  :readonly="readonly"
                >
                </TaskTable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <span class="screenHeading"
                >Helpful Links ({{ community.helpfulLinks ? community.helpfulLinks.length : '0' }})</span
              >
              <v-btn class="ms-2 btn-show" @click="showHelpfulLinks = !showHelpfulLinks" small>{{
                showHelpfulLinks ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="community.helpfulLinks && showHelpfulLinks" style="padding-top: 10px">
                <a v-if="!readonly" class="blue-link" @click="addHelpfulLink()">Add Helpful Link</a>
                <draggable v-model="community.helpfulLinks" @start="drag = true" @end="drag = false">
                  <v-card
                    style="cursor: grab"
                    :style="index != community.helpfulLinks.length - 1 ? 'border-bottom: 0px;' : ''"
                    v-for="(item, index) in community.helpfulLinks"
                    v-bind:key="item.clientID"
                  >
                    <HelpfulLink
                      @deleteHelpfulLink="handleDeleteHelpfulLink"
                      :helpfulLink="item"
                      :index="index"
                      :valid="item.url.trim() != '' && item.display.trim() != ''"
                      :readonly="readonly"
                    ></HelpfulLink>
                  </v-card>
                </draggable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <span class="screenHeading">Email Templates</span>
              <v-btn class="ms-2 btn-show" @click="showEmailTemplates = !showEmailTemplates" small>{{
                showEmailTemplates ? 'Hide' : 'Show'
              }}</v-btn>

              <div v-if="showEmailTemplates" style="padding-top: 10px">
                <SimpleCollapsibleSection v-for="status in notificationStatusConstants" :key="status.value" :title="status.name">
                  <a v-if="!readonly" class="blue-link" @click="addEmailTemplate(status.value,'initials')"
                    >Add Initial Template</a
                  >
                  <div style="padding: 10px;">
                    <EmailTemplate
                      v-for="(email, initialIndex) in community.notificationEmails.initials.filter(
                        x => x.forStatus === status.value
                      )"
                      :key="email.id || email.clientID"
                      emailType="initials"
                      :index="initialIndex"
                      @onDelete="handleEmailTemplateDelete"
                      :title="`Initial email #${initialIndex + 1}`"
                      :notificationEmail="email"
                      allowNameChange
                      :readonly="readonly"
                      @emailTemplateFormChanged="handleEmailTemplateFormChanged"
                      :notificationsEnabled="community.allowNotifications"
                    />
                  </div>
                  <a
                    class="blue-link"
                    @click="addEmailTemplate(status.value, 'reminders')"
                    v-if="readonly ? false : displayAddReminderTemplate(status.value)"
                    >Add Reminder Template</a
                  >
                  <div style="padding: 10px" v-if="displayAddReminderTemplate(status.value)">
                    <EmailTemplate
                      v-for="(email, reminderIndex) in community.notificationEmails.reminders.filter(
                        i => i.forStatus === status.value
                      )"
                      emailType="reminders"
                      :title="`Reminder email #${reminderIndex + 1}`"
                      :index="reminderIndex"
                      :key="email.id || email.clientID"
                      allowDaysIntervalConf
                      @onDelete="handleEmailTemplateDelete"
                      :notificationEmail="email"
                      allowNameChange
                      allowSkip
                      :readonly="readonly"
                      @emailTemplateFormChanged="handleEmailTemplateFormChanged"
                      :notificationsEnabled="community.allowNotifications"
                    />
                  </div>
                </SimpleCollapsibleSection>
                <SimpleCollapsibleSection v-for="tag in notificationTags" :key="tag.key" :title="tag.value">
                  <a
                    class="blue-link"
                    @click="addEmailTemplate(tag.key, 'initials',tag.enabled, true)"
                    v-if="readonly ? false : displayAddInitialTemplateForTag(tag.key)"
                    >Add Template</a
                  >
                  <div style="padding: 10px;" v-if="displayAddInitialTemplateForTag(tag.key)">
                    <EmailTemplate
                      v-for="(email, index) in community.notificationEmails.initials.filter(x => x.tag === tag.key)"
                      :key="email.id || email.clientID"
                      emailType="initials"
                      :index="index"
                      @onDelete="handleEmailTemplateDelete"
                      :title="`Email #${index + 1}`"
                      :notificationEmail="email"
                      allowNameChange
                      :readonly="readonly"
                      @emailTemplateFormChanged="handleEmailTemplateFormChanged"
                      :notificationsEnabled="community.allowNotifications"
                    />
                  </div>
                  <a
                    class="blue-link"
                    @click="addEmailTemplate(tag.key, 'reminders',tag.enabled, true)"
                    v-if="readonly ? false : displayAddReminderTemplateForTag(tag.key)"
                    >Add Reminder Template</a
                  >
                  <div style="padding: 10px" v-if="displayAddReminderTemplateForTag(tag.key)">
                    <EmailTemplate
                      v-for="(email, reminderIndex) in community.notificationEmails.reminders.filter(x => x.tag === tag.key)"
                      emailType="reminders"
                      :title="`Reminder email #${reminderIndex + 1}`"
                      :index="reminderIndex"
                      :key="email.id || email.clientID"
                      allowDaysIntervalConf
                      @onDelete="handleEmailTemplateDelete"
                      :notificationEmail="email"
                      allowNameChange
                      allowSkip
                      :readonly="readonly"
                      @emailTemplateFormChanged="handleEmailTemplateFormChanged"
                      :notificationsEnabled="community.allowNotifications"
                    />
                  </div>
                </SimpleCollapsibleSection>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Fields and Responses ({{ allQuestionsAnswers.length }})</span>
              <v-btn class="ms-2 btn-show" @click="showQASection = !showQASection" small>{{ showQASection ? 'Hide' : 'Show' }}</v-btn>

              <div v-if="showQASection">
                <v-row>
                  <v-col>
                    <a class="blue-link" @click="addQuestion()">Add New Field</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit">
                    <a class="blue-link" @click="bulkAddConfiguration('qa')">Bulk Add Fields and Responses</a>
                  </v-col>
                </v-row>
                <QATable
                  :qa="allQuestionsAnswers"
                  :relationships="community.qaRelationships || []"
                  @editQuestion="editQuestion"
                  @clone="cloneFields"
                  @editAnswerQuestions="editAnswerQuestions"
                />
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Responses Relationships ({{ (community.qaRelationships || []).length }})</span>
              <v-btn class="ms-2 btn-show" @click="showAnswersRelationshipSection = !showAnswersRelationshipSection" small>{{
                showAnswersRelationshipSection ? 'Hide' : 'Show'
              }}</v-btn>
              <div v-if="showAnswersRelationshipSection">
                <v-row>
                  <v-col>
                    <a class="blue-link" @click="addQARelationship()">Add New Response Relationship</a>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col class="text-right" v-if="edit && (community.questions || []).length">
                    <a class="blue-link" @click="bulkAddConfiguration('qaRelationship')">Bulk Add Responses Relationships</a>
                  </v-col>
                </v-row>
                <RelationshipTable
                  :isEditing="edit"
                  :questions="allQuestionsAnswers || []"
                  :relationships="community.qaRelationships || []"
                  :communityAbbreviation="community.communityAbbreviation"
                  :isITSupportOrProcessManager="isITSupportOrProcessManager"
                  @edit="editQARelationship"
                  @delete="deleteResponseRelationship"
                  @clone="cloneRelationship"
                  ref="relationshipTable"
                >
                  <template v-slot:top="props">
                    <v-row justify="end">
                      <v-col cols="12" sm="3" class="relationship-table-actions py-0">
                        <v-btn
                          @click="openFATDialog(props.selected, 'relationships')"
                          class="mr-3"
                          :disabled="!(props.selected && props.selected.length > 0)"
                        >
                          Add Approvers, FYIs, Tasks
                        </v-btn>
                      </v-col>
                    </v-row>
                  </template>
                </RelationshipTable>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-row align="center">
            <v-col cols="12">
              <span class="screenHeading">Display Names</span>
              <v-btn class="ms-2 btn-show" @click="showDNSection = !showDNSection" small>{{ showDNSection ? 'Hide' : 'Show' }}</v-btn>

              <div v-if="showDNSection">
                <KVEdit :readonly="readonly" :data="displayNames"></KVEdit>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid>
          <v-btn v-if="readonly ? false : !edit" :loading="posting" color="success" class="me-2" @click="post">Create</v-btn>
          <v-btn v-if="readonly ? false : edit" :loading="posting" color="success" class="me-2" @click="post">Save</v-btn>
          <v-btn v-on:click="confirm = true" color="info">Cancel</v-btn>
        </v-container>
      </div>
    </v-form>

    <v-dialog persistent v-model="showAnswersRelationshipForm" max-width="1000">
      <QAChooser
        v-if="showAnswersRelationshipForm"
        :qaList="allQuestionsAnswers || []"
        :relationships="community.qaRelationships || []"
        :relationship="selectedQARelationship"
        @cancel="closeQARelationshipForm"
        @save="saveQARelationship"
      >
        <template v-slot:default="props">
          <FAT
            :tasks="community.tasks"
            :attachedTo="props.relationship"
            itemName="relationships"
            @onTaskClick="onTaskClick"
            @onTaskAdd="onTaskAdd"
            :isCommunityConfidential="community.isConfidential"
          />
        </template>
      </QAChooser>
    </v-dialog>

    <v-dialog persistent v-model="showFATDialog" max-width="700">
      <v-card v-if="showFATDialog">
        <v-container>
          <FAT
            :tasks="community.tasks"
            :attachedTo="attachFATTo"
            @onTaskClick="onTaskClick"
            @onTaskAdd="onTaskAdd"
            :isCommunityConfidential="community.isConfidential"
          >
            <template v-slot:default="props">
              <v-card-actions>
                <v-row justify="end">
                  <v-btn color="success" @click="attachFAT(props.attachedTo)">Save</v-btn>
                  <v-btn @click="closeFATDialog()">Cancel</v-btn>
                </v-row>
              </v-card-actions>
            </template>
          </FAT>
        </v-container>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="showQAForm" max-width="1000">
      <QAForm
        v-if="showQAForm"
        @close="showQAForm = false"
        @save="handleSaveQA"
        :fields="getSelectedQuestions"
        :value="selectedAnswer"
        :relationships="community.qaRelationships || []"
      />
    </v-dialog>

    <v-dialog persistent v-model="showChangeTypeModal" max-width="700">
      <ChangeTypeForm
        v-if="showChangeTypeModal"
        @close="handleCloseChangeTypeForm"
        :existingChangeTypes="community.changeTypes"
        :changeType="selectedChangeType"
        @submit="saveChangeType"
        :tasks="community.tasks"
        @onTaskClick="onTaskClick"
        @onTaskAdd="onTaskAdd"
        :shortName="$route.params.shortName"
        :readonly="readonly"
      ></ChangeTypeForm>
    </v-dialog>

    <v-dialog persistent v-model="addOrEditChangeCategory" max-width="700">
      <ChangeCategory
        v-if="addOrEditChangeCategory"
        @addMOCType="handleAddMOCType"
        @deleteChange="handleDeleteChangeCategory"
        @deleteApprovalProcessType="handleDeleteApprovalProcessType"
        @deleteMOCType="handleDeleteMOCType"
        @saveChangeCategory="handleSaveChangeCategory"
        @close="handleCloseChangeCategory"
        :existingTypes="community.mocTypes"
        :changeCategory="selectedChangeCategory"
        :unchanged="unchangedChangeCategory"
        :tasks="community.tasks"
        @onTaskClick="onTaskClick"
        @onTaskAdd="onTaskAdd"
        :readonly="readonly"
      ></ChangeCategory>
    </v-dialog>

    <v-dialog persistent v-model="addOrEditApprovalProcessType" max-width="900px">
      <ApprovalProcessType
        v-if="addOrEditApprovalProcessType"
        :ChangeCategories="community.changeCategories"
        :approvalProcessType="selectedApprovalProcessType"
        :ApprovalTypes="ApprovalTypes"
        @deleteApprovalProcessType="handleDeleteApprovalProcessType"
        @saveApprovalProcessType="handleSaveApprovalProcessType"
        @closeApprovalProcessType="handleCloseApprovalProcessType"
        :isCommunityConfidential="community.isConfidential"
        :hasTemporaryEnabled="hasTemporaryEnabled"
      ></ApprovalProcessType>
    </v-dialog>

    <v-dialog persistent v-model="addOrEditType" max-width="900">
      <AddOrEditMOCType
        v-if="addOrEditType"
        @submit="handleSubmit"
        @close="addOrEditType = false"
        :mocType="selectedMOCType"
        :languages="languages"
        :Create="createType"
        :ExistingTypes="community.mocTypes"
        :readonly="readonly"
      ></AddOrEditMOCType>
    </v-dialog>

    <v-dialog persistent v-model="addOrEditChangeCategoryMOCType" max-width="900">
      <MOCType
        v-if="addOrEditChangeCategoryMOCType"
        @saveMocType="saveChangeCategoryMOCType"
        @close="handleCloseChangeCategoryMOCType"
        :mocType="selectedChangeCategoryMOCType"
        :mocTypeList="community.mocTypes"
        :ChangeCategories="community.changeCategories"
        @deleteMOCType="handleDeleteMOCType"
        :tasks="community.tasks"
        @onTaskClick="onTaskClick"
        @onTaskAdd="onTaskAdd"
        :isCommunityConfidential="community.isConfidential"
        :readonly="readonly"
      ></MOCType>
    </v-dialog>

    <v-dialog persistent v-model="addOrEditTask">
      <Task
        :readonly="readonly"
        v-if="addOrEditTask"
        :task="selectedTask"
        @saveTask="handleSaveTask"
        @closeTask="handleCloseTask"
      ></Task>
    </v-dialog>

    <v-dialog persistent v-model="showBulkAddConfigurationDialog" max-width="800">
      <BulkUploadCsvFile
        v-if="showBulkAddConfigurationDialog"
        @upload="uploadConfigurationFile"
        @close="showBulkAddConfigurationDialog = false"
        :configurationName="fileConfigurationName"
        :communityAbbreviation="community.communityAbbreviation"
      />
    </v-dialog>

    <v-dialog v-model="confirm" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirm cancel</v-card-title>
        <v-card-text>Are you sure you want to cancel your change?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            text="text"
            @click="
              goBack();
              dialog = false
            "
            >Yes</v-btn
          >
          <v-btn color="info" text="text" @click="confirm = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="showConfidentialOffDialog" max-width="500">
      <v-card v-if="showConfidentialOffDialog">
        <v-container>
          <v-alert type="warning"
            >If you continue with this change, this community and its MOCs will be visible to everyone</v-alert
          >
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="red" text @click="confirmConfidentialOffChange">Yes</v-btn>
            <v-btn color="black" text @click="cancelConfidentialChange">No</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="showAuthorizedPeopleRemoveErrorDialog" max-width="600">
      <v-card v-if="showAuthorizedPeopleRemoveErrorDialog">
        <v-container>
          <v-alert type="error">
            To delete this user from the authorized list, please replace it in the configuration.
            <div v-if="edit">
              <p>
                <strong><a href="/userreplacement" class="white--text">Go to user replacement page</a></strong>
              </p>
            </div>
            <span>Found in:</span>
            <ul>
              <li :key="propertyName" v-for="(value, propertyName) in authorizedRemovedUserOcurrencesInConfig">
                <strong>{{ configurationDisplayNames[propertyName] }}</strong>
              </li>
            </ul>
          </v-alert>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn @click="showAuthorizedPeopleRemoveErrorDialog = false">Close</v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <toaster ref="toaster"></toaster>
  </div>
</template>

<style>
/*
this is to get around a bug with vuetify cursor style on chips
apparently it's been fixed with 2.0
Also for some reason you can't used "scoped" with this. sorry!
someone on stackoverflow said this works and it seems OK
*/
.hoverhands .v-chip__content {
  cursor: grab !important;
}

.enabledtype .v-chip__content {
  color: #fff;
}
</style>

<script>
import MOCAdminService from '@/services/admin.service'
import AddOrEditMOCType from '@/components/AddOrEditMOCType'
import ChangeTypeForm from '@/components/ChangeTypeForm'
import MOCType from '@/components/MOCType'
import ChangeCategory from '@/components/ChangeCategory'
import ApprovalProcessType from '@/components/ApprovalProcessType'
import HelpfulLink from '@/components/HelpfulLink'
import { VueDraggableNext } from 'vue-draggable-next'
import Toaster from '@/components/Toaster'
import PeoplePickerMs from '@/components/peoplePicker/PeoplePickerMs'
import QAForm from '@/components/QAForm'
import Task from '@/components/Task'
import EmailTemplate from '@/components/EmailTemplate'
import KVEdit from '@/components/KeyValueEditor.vue'
import SimpleCollapsibleSection from '@/components/SimpleCollapsibleSection'
import QAChooser from '@/components/communityConfig/adminQAActions/QAChooser'
import FAT from '@/components/communityConfig/adminQAActions/FAT'
import RelationshipTable from '@/components/communityConfig/adminQAActions/RelationshipTable'
import QATable from '@/components/communityConfig/adminQA/QATable'
import BulkUploadCsvFile from '@/components/BulkUploadCsvFile'
import { NOTIFICATION_STATUS, NOTIFICATION_TAGS, emailBodyAndSubjectByStatusDefault } from '@/common/notifications'
import { Roles, FormStatus } from '@/common'
import { TaskListTypes, ApprovalTypes, ChangeTypes } from '@/common/types'
import shortid from 'shortid'
import i18next from 'i18next'
import { mapState } from 'vuex'

import ChangeCategoryTable from '@/components/communityConfig/ChangeCategoryTable'
import ApprovalProcessTypeTable from '@/components/communityConfig/ApprovalProcessTypeTable'
import ChangeCategoryMocTypeTable from '@/components/communityConfig/ChangeCategoryMocTypeTable'
import TaskTable from '@/components/communityConfig/TaskTable'

import userReplacementMixins from '@/mixins/userReplacementMixins'

const loadDefaultEmailTemplates = (group, emailType) => {
  if (!group.templates || !group.templates.length) {
    return emailBodyAndSubjectByStatusDefault[emailType]
  }
  const currentTemplatesForEmailType = group.templates.filter(t => t.emailType === emailType)
  return currentTemplatesForEmailType
}

const getNotificationEmailsInitialState = emailType => {
  return NOTIFICATION_STATUS.reduce((acc, status) => {
    const templates = loadDefaultEmailTemplates(status, emailType)

    templates.forEach(t => {
      acc.push({
        clientID: shortid.generate(),
        forStatus: status.value,
        emailType,
        ...(emailType === 'reminders' ? { daysInterval: 5 } : { daysInterval: 0 }),
        enabled: true,
        subject: t.subject,
        template: t.content,
        dirty: true
      })
    })
    return acc
  }, [])
}

const getNotificationTagEmailsInitialState = emailType => {
  return NOTIFICATION_TAGS.reduce((acc, tag) => {
    const templates = loadDefaultEmailTemplates(tag, emailType)

    templates.forEach(t => {
      acc.push({
        clientID: shortid.generate(),
        tag: tag.key,
        emailType,
        ...(emailType === 'reminders' ? { daysInterval: 5 } : { daysInterval: 0 }),
        enabled: tag.enabled,
        subject: t.subject,
        template: t.content,
        dirty: true
      })
    })
    return acc
  }, [])
}

export default {
  name: 'AddOrUpdateCommunity',
  mixins: [userReplacementMixins],
  data: () => {
    return {
      TaskListTypes,
      mocTypesHeaders: [
        {
          title: 'Name',
          key: 'value'
        },
        {
          title: 'Enabled',
          key: 'enabled'
        }
      ],
      changeTypeHeaders: [
        {
          title: 'Name',
          key: 'type'
        },
        {
          title: 'Default',
          key: 'default'
        },
        {
          title: 'Enabled',
          key: 'enabled'
        }
      ],
      ApprovalTypes: Object.values(ApprovalTypes.properties),
      TaskRelatedChangeTypes: Object.values(ChangeTypes.properties),
      showTasks: false,
      addOrEditTask: false,
      selectedTask: {},
      showMocTypes: false,
      showChangeCategories: false,
      showApprovalProcessTypes: false,
      addOrEditType: false,
      addOrEditChangeCategory: false,
      addOrEditApprovalProcessType: false,
      selectedApprovalProcessType: false,
      unchangedTask: {},
      createType: false,
      selectedMOCType: {},
      style: {
        box: false,
        solo: false,
        outline: false
      },
      community: {
        durationInDays: 365,
        communityAbbreviation: null,
        communityName: null,
        mocTypes: [],
        changeCategories: [],
        helpfulLinks: [],
        closers: [],
        emailTemplates: [],
        allowNotifications: true,
        inactive: false,
        newDisabled: false,
        openTextEnabled: false,
        notificationEmails: {
          initials: getNotificationEmailsInitialState('initials').concat(getNotificationTagEmailsInitialState('initials')),
          reminders: getNotificationEmailsInitialState('reminders').concat(getNotificationTagEmailsInitialState('reminders'))
        },
        relationships: [],
        changeTypes: [
          {
            clientID: shortid.generate(),
            type: {
              key: ChangeTypes.TEMPORARY.toString(),
              value: ChangeTypes.properties[ChangeTypes.TEMPORARY].value
            },
            enabled: false,
            default_: false,
            dailyInterval: null,
            tasks: []
          },
          {
            clientID: shortid.generate(),
            type: {
              key: ChangeTypes.PERMANENT.toString(),
              value: ChangeTypes.properties[ChangeTypes.PERMANENT].value
            },
            enabled: true,
            default_: true,
            dailyInterval: null,
            tasks: []
          }
        ],
        displayNames: {}        
      },
      languages: [
        {
          name: 'English',
          value: 'en'
        },
        {
          name: 'Spanish',
          value: 'es'
        }
      ],
      edit: false,
      html: '',
      confirm: false,
      posting: false,
      // people pickers
      callResult: null,
      errorObject: null,
      successMessage: null,
      valid: false,
      isSaving: false,
      dialog: false,
      snackbar: {
        show: false,
        color: '',
        mode: '',
        successTimeout: 2000,
        timeout: 6000,
        text: '',
        icon: null,
        closeIcon: 'close'
      },
      minimumTitleLength: 3,
      maximumTitleLength: 60,
      minimumShortNameLength: 3,
      maximumShortNameLength: 60,
      communities: [],
      gettingCommunity: false,
      approvalProcessTypes: [],
      unchangedChangeCategory: {},
      showChangeCategoryMOCType: false,
      addOrEditChangeCategoryMOCType: false,
      mocTypes: [],
      selectedChangeCategoryMOCType: {},
      showHelpfulLinks: false,
      showEmailTemplates: false,
      notificationStatusConstants: NOTIFICATION_STATUS,
      notificationTags: NOTIFICATION_TAGS,
      selectedChangeType: {},
      showChangeTypeSection: false,
      showChangeTypeModal: false,
      showQAForm: false,
      showQASection: false,
      showDNSection: false,
      selectedQuestion: {},
      allQuestionsAnswers: [],
      selectedAnswer: {},
      showAnswersRelationshipSection: false,
      showAnswersRelationshipForm: false,
      selectedQARelationship: {},
      showFATDialog: false,
      attachFATTo: {},
      loading: false,
      showBulkAddConfigurationDialog: false,
      fileConfigurationName: '',
      displayNames: [],
      tempAuthorizedPeople: [],
      authorizedPeoplePickerConfidentialOptions: {},
      showConfidentialOffDialog: false,
      originallyConfidentialCommunity: true,
      authorizedRemovedUserOcurrencesInConfig: {},
      showAuthorizedPeopleRemoveErrorDialog: false
    }
  },
  computed: {
    ...mapState('currentUserAdminRoles', ['adminRoles']),
    readonly() {
      if (
        this.adminRoles.findIndex(
          x => x.toLowerCase() === 'processmanager' || x.toLowerCase() === 'itsupport' || x.toLowerCase() === 'communityowner'
        ) > -1
      ) {
        return false
      }
      return true
    },
    hasTemporaryEnabled() {
      const temporary = this.community.changeTypes.find(changeType => changeType.type.key === ChangeTypes.TEMPORARY)
      return temporary.enabled
    },
    isITSupportOrProcessManager() {
      return this.adminRoles.findIndex(x => x.toLowerCase() === 'processmanager' || x.toLowerCase() === 'itsupport') > -1
    },
    showConfidentialCheck() {
      return (this.$route.params.shortName && !this.originallyConfidentialCommunity) || !this.edit
    },
    currentUser() {
      return this.$store.state.currentUser.user
    },
    notificationsOverriden() {
      return this.community.overrideNotifications
    },
    overrideNotificationsToRules() {
      return [
        v => (v && v.trim() ? true : 'Email to override notifications is required'),
        v => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'Email is invalid'
      ]
    },
    closerRoles() {
      return Object.values(Roles.properties).filter(r => ['owner', 'facilitator', 'coordinator'].includes(r.value))
    },
    fullValid: {
      cache: false,
      get: function() {
        return (
          this.valid &&
          !this.community.changeCategories.find(
            item =>
              !item.formValid ||
              item.facilitator == null ||
              // validate approval process types
              item.approvalProcessTypes.find(x => !x.formValid || !x.approvalType) ||
              // validate moc types
              item.mocTypes.find(x => !x.formValid || !x.value)
          ) &&
          !this.community.helpfulLinks.find(item => !item.display.trim() || !item.url.trim())
        )
      }
    },
    titleRules: {
      get: function() {
        return [
          v => !!v || 'Title is required',
          v => (v && v.length >= this.minimumTitleLength) || `Minimum length is ${this.minimumTitleLength}`,
          v => (v && v.length <= this.maximumTitleLength) || `Maximum length is ${this.maximumTitleLength}`
        ]
      }
    },
    shortNameRules: {
      get: function() {
        return [
          v => !!v || 'Short Name is required',
          v => (v && v.trim().length === this.minimumShortNameLength) || `Length must be ${this.minimumShortNameLength}`,
          v => (v && !this.isValidAbbreviation(v)) || `Valid characters are A-Z, a-z, 0-9 or _`
        ]
      }
    },
    durationInDaysRules: {
      get: function() {
        return [v => (v && !isNaN(parseFloat(v)) && v >= 1 && v <= 999) || `Days must be between 1 and 999`]
      }
    },
    getSelectedQuestions() {
      return [this.selectedQuestion]
    }
  },
  methods: {
    cancelConfidentialChange() {
      this.community.isConfidential = true
      this.showConfidentialOffDialog = false
    },
    confirmConfidentialOffChange() {
      this.community.isConfidential = false
      this.showConfidentialOffDialog = false
      this.tempAuthorizedPeople = []
      this.community.authorizedPeople = []
      this.postOrPut()
    },
    getPeoplePickerInjectedProperties() {
      return {
        confidentialOptions: {
          authorizedPeople: this.community.isConfidential ? this.community.authorizedPeople : undefined
        }
      }
    },
    onConfidentialityChange(value) {
      if (value) {
        this.community.authorizedPeople = this.getPeopleInCommunity(this.community)
        this.tempAuthorizedPeople = JSON.parse(JSON.stringify(this.community.authorizedPeople))
        this.community.authorizedPeople = this.tempAuthorizedPeople
      }

      if ((!value && !this.originallyConfidentialCommunity) || !this.edit) {
        this.community.authorizedPeople = []
        this.tempAuthorizedPeople = []
        this.community.authorizedPeople = this.tempAuthorizedPeople
        return
      }
      if (value || !this.originallyConfidentialCommunity) {
        return
      }

      this.showConfidentialOffDialog = true
    },
    changeOverrideNotifications(value) {
      if (value) {
        this.setUserOverrideNotificationsTo()
      } else {
        this.communities.overrideNotificationsTo = undefined
      }
    },
    bulkAddConfiguration(configurationName) {
      this.showBulkAddConfigurationDialog = true
      this.fileConfigurationName = configurationName
    },
    uploadConfigurationFile() {
      this.getData(this.community.communityAbbreviation)
    },
    displayAddReminderTemplate(status) {
      const row = NOTIFICATION_STATUS.find(t => t.value === status) || {}
      return !row.hideReminders
    },
    displayAddReminderTemplateForTag(tagKey) {
      const row = NOTIFICATION_TAGS.find(t => t.key === tagKey) || {}
      return !row.hideReminders
    },
    displayAddInitialTemplateForTag(tagKey) {
      const row = NOTIFICATION_TAGS.find(t => t.key === tagKey) || {}
      return !row.hideInitials
    },
    isValidAbbreviation(abbreviation) {
      var patt = new RegExp('[^A-Za-z0-9_]+')
      var res = patt.test(abbreviation)
      return res
    },
    cloneRelationship(relationship) {
      const selectedRelationship = JSON.parse(
        JSON.stringify(this.community.qaRelationships.find(r => r.clientId === relationship.clientId))
      )
      this.selectedQARelationship = {
        ...selectedRelationship,
        clientId: shortid.generate(),
        id: undefined
      }

      this.showAnswersRelationshipForm = true
    },
    deleteResponseRelationship(relationship) {
      const index = this.community.qaRelationships.findIndex(r => r.clientId === relationship.clientId)
      if (index > -1) {
        this.community.qaRelationships.splice(index, 1)
        this.postOrPut()
      }
    },
    attachFAT(attachedTo) {
      if (attachedTo.item === 'relationships') {
        this.community.qaRelationships.forEach(r => {
          if (attachedTo.selected.findIndex(s => s.clientId === r.clientId) !== -1) {
            const approversIds = attachedTo.approvers.map(r => r.id)
            const fyisIds = attachedTo.fyis.map(r => r.id)
            r.approvers = r.approvers.filter(a => !approversIds.includes(a.id)).concat(attachedTo.approvers)
            r.fyis = r.fyis.filter(a => !fyisIds.includes(a.id)).concat(attachedTo.fyis)
            r.tasks = r.tasks.filter(t => !attachedTo.tasks.includes(t)).concat(attachedTo.tasks)
          }
        })
        this.$refs.relationshipTable.cleanSelected()
      } else if (attachedTo.item === 'changeCategory') {
        this.community.changeCategories.forEach(changeCategory => {
          if (attachedTo.selected.findIndex(s => s.clientID === changeCategory.clientID) !== -1) {
            changeCategory.tasks = (changeCategory.tasks || [])
              .filter(t => !attachedTo.tasks.includes(t))
              .concat(attachedTo.tasks)
          }
        })
      } else if (attachedTo.item === 'approvalProcessTypes') {
        this.community.changeCategories.forEach(changeCategory => {
          ;(changeCategory.approvalProcessTypes || []).forEach(approvalProcessType => {
            if (attachedTo.selected.findIndex(s => s.clientID === approvalProcessType.clientID) !== -1) {
              const approversIds = attachedTo.approvers.map(r => r.id)
              const fyisIds = attachedTo.fyis.map(r => r.id)
              approvalProcessType.approvers = (approvalProcessType.approvers || [])
                .filter(a => !approversIds.includes(a.id))
                .concat(attachedTo.approvers)
              approvalProcessType.fyis = (approvalProcessType.fyis || [])
                .filter(a => !fyisIds.includes(a.id))
                .concat(attachedTo.fyis)
            }
          })
        })
      } else if (attachedTo.item === 'changeCategoryMocTypes') {
        this.community.changeCategories.forEach(changeCategory => {
          ;(changeCategory.mocTypes || []).forEach(mocType => {
            if (attachedTo.selected.findIndex(s => s.clientID === mocType.clientID) !== -1) {
              const approversIds = attachedTo.approvers.map(r => r.id)
              const fyisIds = attachedTo.fyis.map(r => r.id)
              mocType.approvers = (mocType.approvers || []).filter(a => !approversIds.includes(a.id)).concat(attachedTo.approvers)
              mocType.fyis = (mocType.fyis || []).filter(a => !fyisIds.includes(a.id)).concat(attachedTo.fyis)
              mocType.tasks = (mocType.tasks || []).filter(t => !attachedTo.tasks.includes(t)).concat(attachedTo.tasks)
            }
          })
        })
      }

      this.showFATDialog = false
      this.attachFATTo = {
        approvers: [],
        tasks: [],
        fyis: [],
        selected: []
      }
      this.postOrPut()
    },

    cloneFields(selectedField, answerTarget, includeChildren) {
      const questionsList = JSON.parse(JSON.stringify(this.community.questions || []))
      this.community.questions = questionsList.reduce((acc, cur) => {
        this.cloneFieldsInner(cur.values, selectedField, answerTarget, includeChildren)
        acc = acc.concat(cur)
        return acc
      }, [])
      this.postOrPut()
      if (!this.edit) {
        this.getQA()
      }
    },
    cloneFieldsInner(values, selectedField, answerTarget, includeChildren) {
      return values.map(v => {
        if (answerTarget.clientId === v.clientId) {
          let fieldToClone = JSON.parse(JSON.stringify(selectedField))
          fieldToClone.id = undefined
          fieldToClone.clientId = shortid.generate()
          fieldToClone.order = v.questions.length + 1
          fieldToClone.label = 'Copy of ' + fieldToClone.label
          if (includeChildren) {
            fieldToClone.values = this.getSelectedAnswerToCloneChildren(fieldToClone.values)
            v.questions.push(fieldToClone)
          } else {
            fieldToClone.values = fieldToClone.values.map((qv, i) => ({
              ...qv,
              questions: [],
              clientId: shortid.generate(),
              id: undefined
            }))
            v.questions.push(fieldToClone)
          }
        } else {
          for (let question of v.questions) {
            this.cloneFieldsInner(question.values, selectedField, answerTarget, includeChildren)
          }
        }
        return v
      })
    },
    getSelectedAnswerToCloneChildren(values) {
      return values.map(v => {
        for (let question of v.questions) {
          question.id = undefined
          question.clientId = shortid.generate()
          question.values = this.getSelectedAnswerToCloneChildren(question.values)
          question.label = 'Copy of ' + question.label
        }
        return {
          ...v,
          clientId: shortid.generate(),
          id: undefined
        }
      })
    },
    closeFATDialog() {
      this.attachFATTo = {
        approvers: [],
        tasks: [],
        fyis: [],
        selected: []
      }
      this.showFATDialog = false
    },
    openFATDialog(selected, item) {
      if (!selected.length) return
      this.attachFATTo = {
        approvers: [],
        tasks: [],
        fyis: [],
        selected,
        item
      }
      this.showFATDialog = true
    },
    closeQARelationshipForm() {
      this.selectedQARelationship = {}
      this.showAnswersRelationshipForm = false
    },
    saveQARelationship(relationship) {
      if (!this.community.qaRelationships) {
        this.community.qaRelationships = []
      }

      //sort approvers and fyis
      relationship.approvers.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
      relationship.fyis.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))

      if (this.community.qaRelationships.findIndex(r => r.clientId === relationship.clientId) > -1) {
        this.community.qaRelationships = this.community.qaRelationships.map(r => {
          if (r.clientId === relationship.clientId) return relationship
          return r
        })
      } else {
        this.community.qaRelationships.push(relationship)
      }
      this.postOrPut()
      this.selectedQARelationship = {}
      this.showAnswersRelationshipForm = false
    },
    addQARelationship() {
      this.selectedQARelationship = {
        clientId: shortid.generate(),
        criteria: [],
        approvers: [],
        tasks: [],
        fyis: []
      }
      this.showAnswersRelationshipForm = true
    },
    editQARelationship(relationship) {
      this.selectedQARelationship = JSON.parse(
        JSON.stringify(this.community.qaRelationships.find(r => r.clientId === relationship.clientId))
      )
      this.showAnswersRelationshipForm = true
    },
    validateClosers() {
      return (this.community.closers || []).length > 0 ? null : 'At least one closer is required'
    },
    getQA() {
      const sortedQuestions = this.$_.orderBy(this.community.questions, ['order'], ['asc'])
      console.log(`ommunity.questions sortedQuestions ${JSON.stringify(sortedQuestions)}`)
      this.allQuestionsAnswers = sortedQuestions
        .reduce((acc, cur) => {
          cur.clientId = cur.id ? cur.id : cur.clientId
          cur.values = this.$_.orderBy(cur.values, ['order'], ['asc'])
          const result = this.getQuestionsFromAnswers(cur, cur.values, cur)
          return acc.concat(result)
        }, [])
        .map(i => {
          const parentFullLabel = this.getQALabel(i.parentQuestion)
          return {
            ...i,
            fullLabel: parentFullLabel + ' / ' + (i.enabled ? '' : '(Disabled) ') + i.label,
            parentQuestion: {
              ...i.parentQuestion,
              fullLabel: parentFullLabel
            }
          }
        })
    },
    getQALabel(question) {
      if (!(question.questionHierarchy || []).length) return question.label
      const hierarchyLength = (question.questionHierarchy || []).length + (question.answerHierarchy || []).length
      let list = []
      for (let i = 0; i < hierarchyLength; i++) {
        if (question.questionHierarchy[i]) list.push(question.questionHierarchy[i].label)
        if (question.answerHierarchy[i]) list.push(question.answerHierarchy[i].label)
      }

      list.push(question.label)

      return (question.enabled ? '' : '(Disabled) ') + list.join(' / ')
    },
    getQuestionsFromAnswers(root = {}, values = [], parentQuestion = {}, questionHierarchy = [], answerHierarchy = []) {
      if (!values.length) {
        return [{ parentQuestion }]
      }
      return values.reduce((a, c) => {
        c.clientId = c.id ? c.id : c.clientId
        a = a.concat({
          parentQuestion,
          ...c
        })
        const sortedQuestions = this.$_.orderBy(c.questions, ['order'], ['asc'])
        for (let cur of sortedQuestions) {
          answerHierarchy = answerHierarchy
            .filter(a => a.parentQuestionClientId !== parentQuestion.clientId)
            .concat({
              label: c.label,
              clientId: c.clientId,
              parentQuestionClientId: parentQuestion.clientId
            })

          questionHierarchy = questionHierarchy
            .filter(c => c.clientId !== parentQuestion.clientId)
            .concat({ label: parentQuestion.label, clientId: parentQuestion.clientId })

          cur.clientId = cur.id ? cur.id : cur.clientId
          cur.values = this.$_.orderBy(cur.values, ['order'], ['asc'])
          cur.questionHierarchy = questionHierarchy
          cur.answerHierarchy = answerHierarchy
          a = a.concat(this.getQuestionsFromAnswers(root, cur.values, cur, questionHierarchy, answerHierarchy))
        }
        return a
      }, [])
    },
    editQuestion(question) {
      this.selectedAnswer = {}
      this.selectedQuestion = JSON.parse(JSON.stringify(question))
      this.showQAForm = true
    },
    editAnswerQuestions(answer) {
      this.selectedQuestion = {}
      this.selectedAnswer = JSON.parse(JSON.stringify(answer))
      this.showQAForm = true
    },
    replaceQuestion(values, questions) {
      return values.reduce((a, c) => {
        if (c.clientId === this.selectedAnswer.clientId) {
          c.questions = questions
          return a
        }
        for (let cur of c.questions) {
          if (cur.clientId === this.selectedQuestion.clientId) {
            if (!questions.length) {
              c.questions = c.questions.filter(c => c.clientId !== this.selectedQuestion.clientId)
              break
            }
            for (let question of questions) {
              const found = c.questions.find(q => q.clientId === question.clientId)
              if (found) {
                c.questions = c.questions.map(q => {
                  if (q.clientId === found.clientId) return question
                  return q
                })
              } else {
                c.questions.push(question)
              }
            }
            break
          }
          a = a.concat(this.replaceQuestion(cur.values, questions))
        }
        return a
      }, [])
    },
    handleSaveQA(questions, relationshipsToDelete) {
      const questionsList = JSON.parse(JSON.stringify(this.community.questions || []))
      if (!questionsList.length) {
        this.attachToMain = false
        this.community.questions = questionsList.concat(questions)
      } else {
        this.community.questions = questionsList.reduce((acc, cur) => {
          if (cur.clientId === this.selectedQuestion.clientId) {
            acc = acc.concat(questions)
            return acc
          }
          this.replaceQuestion(cur.values, questions)
          acc = acc.concat(cur)

          if (this.attachToMain) {
            this.attachToMain = false
            for (let question of questions) {
              const found = questionsList.find(q => q.clientId === question.clientId)
              if (!found) acc.push(question)
            }
          }
          return acc
        }, [])
      }
      if (relationshipsToDelete.length) {
        const toDelete = relationshipsToDelete.map(r => r.relationshipId)
        this.community.qaRelationships = this.community.qaRelationships.filter(r => !toDelete.includes(r.clientId))
      }
      this.postOrPut()

      if (!this.edit) {
        this.getQA()
      }
      this.selectedQuestion = {}
      this.selectedAnswer = {}
      this.showQAForm = false
    },
    addQuestion() {
      this.selectedAnswer = {}
      this.selectedQuestion = {
        clientId: new Date().getTime(),
        label: '',
        order: (this.community.questions || []).length || 1,
        enabled: true,
        values: []
      }
      this.attachToMain = true
      this.showQAForm = true
    },
    handleCloseChangeTypeForm() {
      this.showChangeTypeModal = false
    },
    addChangeType() {
      const changeType = {
        clientID: new Date().getTime(),
        type: '',
        default_: false,
        enabled: true
      }
      this.selectedChangeType = changeType
      this.showChangeTypeModal = true
    },
    editChangeType(changeType) {
      this.selectedChangeType = JSON.parse(JSON.stringify(changeType))
      this.showChangeTypeModal = true
    },
    saveChangeType(changeType) {
      const index = this.community.changeTypes.findIndex(c => c.clientID === changeType.clientID)
      if (index < 0) {
        this.community.changeTypes.push(changeType)
      } else {
        this.community.changeTypes[index] = changeType
      }
      this.postOrPut()
      this.showChangeTypeModal = false
    },
    onTaskClick(task) {
      this.editTask(task)
    },
    onTaskAdd(search, onSave) {
      this.addOrEditTask = true
      this.selectedTask = {
        mustComplete: true,
        active: true,
        name: search,
        clientID: new Date().getTime(),
        onSave
      }
    },
    saveChangeCategoryMOCType: function(changeCategoryMOCType) {
      const selectedMocType = this.community.mocTypes.find(m => m.clientID === changeCategoryMOCType.id)
      changeCategoryMOCType.value = selectedMocType.value
      changeCategoryMOCType.isNew = !this.edit

      if (changeCategoryMOCType.changeCategories && changeCategoryMOCType.changeCategories.length) {
        for (let changeCategory of changeCategoryMOCType.changeCategories) {
          let existingChangeCategory = this.community.changeCategories.find(x => x.clientID === changeCategory.clientID)
          existingChangeCategory.mocTypes.push({
            id: changeCategoryMOCType.id,
            value: changeCategoryMOCType.value,
            isNew: changeCategoryMOCType.isNew,
            changeCategory: {
              clientID: changeCategory.clientID
            },
            approvers: changeCategoryMOCType.approvers,
            fyis: changeCategoryMOCType.fyis,
            tasks: changeCategoryMOCType.tasks
          })
        }
      } else {
        let changeCategory = this.community.changeCategories.find(
          x => x.clientID === changeCategoryMOCType.changeCategory.clientID
        )

        if (!changeCategory) {
          return
        }

        // sort approvers and fyis
        changeCategoryMOCType.approvers.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
        changeCategoryMOCType.fyis.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))

        const index = changeCategory.mocTypes.findIndex(m => m.id === changeCategoryMOCType.id)
        if (index < 0) {
          changeCategory.mocTypes.push(changeCategoryMOCType)
        } else {
          changeCategory.mocTypes[index] = changeCategoryMOCType
        }
      }

      this.postOrPut()
      this.setGlobalChangeCategoryMocTypeList()
      this.addOrEditChangeCategoryMOCType = false
    },
    handleCloseTask: function() {
      this.addOrEditTask = false
    },
    handleCloseChangeCategoryMOCType: function(mocType) {
      this.addOrEditChangeCategoryMOCType = false
    },
    handleSaveApprovalProcessType: function(approvalProcessType) {
      let changeCategory = this.community.changeCategories.find(x => x.clientID === approvalProcessType.changeCategory.clientID)
      if (!changeCategory) {
        //couldn't find the change category here.. something's wrong
        return
      }
      const index = changeCategory.approvalProcessTypes.findIndex(a => a.clientID === approvalProcessType.clientID)
      if (index < 0) {
        changeCategory.approvalProcessTypes.push(approvalProcessType)
      } else {
        changeCategory.approvalProcessTypes[index] = approvalProcessType
      }
      this.postOrPut()
      this.setGlobalApprovalProcessTypeList()
      this.addOrEditApprovalProcessType = false
    },
    handleSaveTask: function(task) {
      if (!this.community.tasks) {
        this.community.tasks = []
      }
      let idx = this.community.tasks.findIndex(x => x.clientID === task.clientID)

      if (idx >= 0) this.community.tasks.splice(idx, 1)
      this.community.tasks.push(task)
      var promise = this.postOrPut()
      if (promise && promise.then && this.edit) {
        promise.then(() => {
          const taskRecentlyAdded = this.community.tasks.find(t => t.originalClientId === task.clientID.toString())
          if (task.onSave && taskRecentlyAdded) task.onSave(taskRecentlyAdded)
        })
      } else {
        if (task.onSave) task.onSave(task)
      }

      this.addOrEditTask = false
    },
    handleUpdateOwners: function(usersAction) {
      this.community.owners = usersAction.Users
    },
    handleUpdateLeadFacilitators(usersAction) {
      this.community.leadFacilitators = usersAction.Users
    },
    handleUpdateAuthorizedPeople: function(usersAction) {
      this.community.authorizedPeople = usersAction.Users
    },
    handleRemoveAuthorizedPeople: function(removedUser, people, done) {
      this.authorizedRemovedUserOcurrencesInConfig = this.findUserInCommunity(this.community, removedUser)
      if (!Object.keys(this.authorizedRemovedUserOcurrencesInConfig).length) {
        done()
        return
      }
      this.showAuthorizedPeopleRemoveErrorDialog = true
    },
    setGlobalApprovalProcessTypeList() {
      this.approvalProcessTypes = []

      this.community.changeCategories.forEach(change => {
        ;(change.approvalProcessTypes || []).forEach(apptype => {
          apptype.changeCategory = {
            name: change.name,
            enabled: change.enabled,
            clientID: change.clientID
          }
          this.approvalProcessTypes.push(apptype)
        })
      })
    },
    postOrPut() {
      let promise
      if (this.edit) {
        this.loading = true
        if (this.readonly) {
          promise = this.partialUpdate()
        } else {
          promise = this.put()
        }
      }
      if (promise && promise.finally) {
        promise.finally(() => (this.loading = false))
      } else {
        this.loading = false
      }
      return promise
    },
    setGlobalChangeCategoryMocTypeList() {
      this.mocTypes = []
      this.community.changeCategories.forEach(change => {
        ;(change.mocTypes || []).forEach(moctype => {
          moctype.changeCategory = {
            name: change.name,
            enabled: change.enabled,
            clientID: change.clientID
          }
          this.mocTypes.push(moctype)
        })
      })
    },
    toggleTemporary() {
      this.community.changeTypes[1].enabled = !this.community.changeTypes[0].enabled
    },
    goBack() {
      this.$router.push('/communities')
    },
    addHelpfulLink() {
      this.community.helpfulLinks.push({
        clientID: new Date().getTime(),
        display: '',
        url: ''
      })
    },
    editChangeCategory(changeCategory) {
      this.selectedChangeCategory = JSON.parse(JSON.stringify(changeCategory))
      this.addOrEditChangeCategory = true
    },
    editApprovalProcessType(approvalProcessType) {
      this.selectedApprovalProcessType = JSON.parse(JSON.stringify(approvalProcessType))
      this.addOrEditApprovalProcessType = true
    },
    addChangeCategory() {
      var newChangeCategory = {
        clientID: new Date().getTime(),
        name: '',
        facilitator: null,
        facilitatorBackup: null,
        approvalProcessTypes: [],
        mocTypes: [],
        labels: [],
        thresholds: [],
        roles: [],
        tasks: [],
        answerFilters: [],
        expanded: false,
        enabled: true
      }
      if (this.community.changeCategories.length === 0) {
        newChangeCategory.displayOrder = 0
      }
      this.selectedChangeCategory = newChangeCategory
      this.addOrEditChangeCategory = true
    },
    addType() {
      this.addOrEditType = true
      this.createType = true
      var blankTranslations = {}
      this.languages.forEach(function(element) {
        blankTranslations[element.value] = ''
      })
      this.selectedMOCType = {
        clientID: new Date().getTime(),
        value: '',
        // translation: blankTranslations,
        enabled: true
      }
    },
    addTask() {
      this.addOrEditTask = true
      this.selectedTask = {
        active: true,
        clientID: new Date().getTime()
      }
    },
    editTask(item) {
      this.unchangedTask = JSON.parse(JSON.stringify(item))
      this.selectedTask = item
      this.addOrEditTask = true
    },
    editType(item) {
      this.selectedMOCType = JSON.parse(JSON.stringify(item)) //make a copy
      this.addOrEditType = true
      this.createType = false
    },
    editChangeCategoryMOCType(item) {
      this.selectedChangeCategoryMOCType = JSON.parse(JSON.stringify(item))
      this.addOrEditChangeCategoryMOCType = true
    },
    addApprovalProcessType() {
      let newApprovalProcessType = {
        clientID: new Date().getTime(),
        name: '',
        owner: null,
        approvalDays: 0,
        approvalType: null,
        approvers: [],
        fyis: [],
        expanded: false,
        changeCategory: {},
        lineManager: undefined,
        vp: undefined,
        multiPass: {
          enabled: false,
          approvalPasses: [],
          outletStatus: FormStatus.properties[FormStatus.SUBMITTED]
        }
      }
      this.selectedApprovalProcessType = newApprovalProcessType
      this.addOrEditApprovalProcessType = true
    },
    addChangeCategoryMOCType() {
      let newMOCType = {
        clientID: '',
        approvers: [],
        fyis: [],
        changeCategories: [],
        changeCategory: {},
        isNew: true
      }
      this.selectedChangeCategoryMOCType = newMOCType
      this.addOrEditChangeCategoryMOCType = true
    },
    handleAddMOCType(item) {
      var newMOCType = {
        clientID: new Date().getTime(),
        mocTypeID: null,
        tasks: [],
        approvers: [],
        fyis: [],
        expanded: false,
        formValid: false
      }
      item.mocTypes.push(newMOCType)
    },
    handleCloseApprovalProcessType() {
      this.addOrEditApprovalProcessType = false
    },
    handleDeleteHelpfulLink(item) {
      this.community.helpfulLinks.splice(this.community.helpfulLinks.findIndex(x => x.clientID === item.clientID), 1)
    },
    handleDeleteChangeCategory(item) {
      const index = this.community.changeCategories.findIndex(x => x.clientID === item.clientID)
      if (index < 0) {
        this.addOrEditChangeCategory = false
        this.selectedChangeCategory = {}
        return
      }
      this.community.changeCategories.splice(index, 1)
      this.setGlobalApprovalProcessTypeList()
      this.setGlobalChangeCategoryMocTypeList()
      this.addOrEditChangeCategory = false
      this.selectedChangeCategory = {}
    },
    handleDeleteMOCType(mocType) {
      var cc = this.community.changeCategories.find(x => x.clientID === mocType.changeCategory.clientID)
      if (!cc) {
        this.addOrEditChangeCategoryMOCType = false
        return
      }
      const index = cc.mocTypes.findIndex(x => x.id === mocType.id)
      if (index < 0) {
        this.addOrEditChangeCategoryMOCType = false
        return
      }
      cc.mocTypes.splice(index, 1)
      this.postOrPut()
      this.setGlobalChangeCategoryMocTypeList()
      this.addOrEditChangeCategoryMOCType = false
    },
    handleDeleteApprovalProcessType(approvalProcessType) {
      var cc = this.community.changeCategories.find(x => x.clientID === approvalProcessType.changeCategory.clientID)
      if (!cc) {
        this.addOrEditApprovalProcessType = false
        return
      }
      const index = cc.approvalProcessTypes.findIndex(x => x.clientID === approvalProcessType.clientID)
      if (index < 0) {
        this.addOrEditApprovalProcessType = false
        return
      }
      cc.approvalProcessTypes.splice(index, 1)
      this.postOrPut()
      this.setGlobalApprovalProcessTypeList()
      this.addOrEditApprovalProcessType = false
    },
    handleDeleteChangeCategoryMocType(changeCategoryMocType) {
      var cc = this.community.changeCategories.find(x => x.clientID === changeCategoryMocType.changeCategory.clientID)
      const index = cc.mocTypes.findIndex(x => x.clientID === changeCategoryMocType.clientID)
      if (index < 0) {
        return
      }
      cc.mocTypes.splice(index, 1)
      this.postOrPut()
    },
    handleSubmit(obj) {
      obj.value = obj.value.trim()
      if (!this.createType) {
        this.community.mocTypes.splice(this.community.mocTypes.findIndex(x => x.clientID === obj.clientID), 1, obj)
        this.community.changeCategories.forEach(c => {
          ;(c.mocTypes || []).forEach(m => {
            if (m.id === obj.clientID) m.value = obj.value
          })
        })
      } else {
        this.community.mocTypes.push(obj)
      }
      this.addOrEditType = false
      this.postOrPut()
    },
    handleClose() {
      this.addOrEditType = false
    },
    handleCloseChangeCategory() {
      this.selectedChangeCategory = {}
      this.addOrEditChangeCategory = false
    },
    handleSaveChangeCategory(changeCategory) {
      const idx = this.community.changeCategories.findIndex(x => x.clientID === changeCategory.clientID)
      if (idx >= 0) {
        this.community.changeCategories[idx] = changeCategory
      } else {
        this.community.changeCategories.push(changeCategory)
      }
      this.postOrPut()
      this.setGlobalApprovalProcessTypeList()
      this.setGlobalChangeCategoryMocTypeList()
      this.addOrEditChangeCategory = false
    },
    addEmailTemplate(group, emailType,enabled=true, isTag = false) {
      if (
        this.community.notificationEmails[emailType].filter(c => (isTag ? c.tag === group : c.forStatus === group)).length >= 10
      ) {
        return
      }
      this.community.notificationEmails[emailType].push({
        ...(isTag ? { tag: group } : { forStatus: group }),
        clientID: shortid.generate(),
        emailType,
        enabled: enabled
      })
    },
    handleEmailTemplateFormChanged({ emailType, ...params }, clientID) {
      const index = this.community.notificationEmails[emailType].findIndex(c => c.clientID === clientID)
      this.community.notificationEmails[emailType][index] = {
        ...(this.community.notificationEmails[emailType][index] || {}),
        ...params
      }
    },
    handleEmailTemplateDelete(clientID, emailType) {
      this.community.notificationEmails[emailType] = this.community.notificationEmails[emailType].filter(
        i => i.clientID !== clientID
      )
    },
    validateEmailTemplateContent(parser, current) {
      const { textContent } = parser.parseFromString(current.template, 'text/html').documentElement
      return (
        current.to &&
        current.to.length > 0 &&
        current.subject &&
        current.subject.trim() &&
        textContent.trim() &&
        current.daysInterval >= 0
      )
    },
    isEmailTemplateFormValid() {
      if (!this.community.allowNotifications) return true
      const parser = new DOMParser()
      const keys = Object.keys(this.community.notificationEmails)
      for (let key of keys) {
        const current = this.community.notificationEmails[key]
        if (typeof current !== 'object') continue
        if (Array.isArray(current)) {
          const emails = current.filter(c => c.enabled)
          for (let cur of emails) {
            if (!this.validateEmailTemplateContent(parser, cur)) {
              return false
            }
          }
        } else {
          if (current.enabled && !this.validateEmailTemplateContent(parser, current)) {
            return false
          }
        }
      }

      return true
    },
    post: function() {
      if (!this.valid) {
        this.$refs.toaster.pop('error', 'Please check errors in form')
        return
      }
      if (!this.isEmailTemplateFormValid()) {
        this.$refs.toaster.pop('error', 'Please check for errors in the Email Templates section')
        return
      }
      const closerValidation = this.validateClosers()
      if (typeof closerValidation === 'string') {
        this.$refs.toaster.pop('error', closerValidation)
        return
      }

      if (
        !this.community.communityAbbreviation &&
        (this.community.communityAbbreviation && !this.community.communityAbbreviation.trim())
      ) {
        this.$refs.toaster.pop('error', 'Abbreviation is required')
        return
      }

      if ((this.community.helpfulLinks || []).length > 0) {
        this.community.helpfulLinks.forEach(t => {
          t.url = t.url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
        })
      }

      if (!this.posting) {
        if (!this.edit) {
          this.posting = true
          this.community.displayNames = this.displayNames
          return MOCAdminService.postCommunity(this.community)
            .then(() => {
              this.$refs.toaster.pop('success', 'Community created')
              this.edit = true
              this.$router.push({ path: `${this.community.communityAbbreviation}/CommunityConfig/` })
            })
            .catch(err => {
              this.$refs.toaster.pop('error', err.response.data.message ? err.response.data.message : 'Create failed')
            })
            .finally(() => {
              this.posting = false
            })
        } else {
          return this.put()
        }
      }
    },
    put: function() {
      if (!this.posting && this.community.id) {
        this.posting = true
        this.community.displayNames = this.displayNames
        return MOCAdminService.updateCommunity(this.community)
          .then(result => {
            this.dialog = false
            this.mapCommunity(result.community)
            this.$refs.toaster.pop('success', 'Updated')
          })
          .catch(err => {
            this.$refs.toaster.pop('error', err.response.data.message ? err.response.data.message : 'Update failed')
          })
          .finally(() => {
            this.posting = false
          })
      }
    },
    partialUpdate() {
      if (!this.posting && this.community.id) {
        this.posting = true
        this.community.displayNames = this.displayNames
        return MOCAdminService.partialUpdate(this.community.communityAbbreviation, {
          communityAbbreviation: this.community.communityAbbreviation,
          qaRelationships: this.community.qaRelationships,
          questions: this.community.questions,
          changeCategories: this.community.changeCategories.map(c => ({
            id: c.id,
            approvalProcessTypes: c.approvalProcessTypes
          })),
          modifiedByDate: this.community.modifiedByDate
        })
          .then(result => {
            this.dialog = false
            this.mapCommunity(result.community)
            this.$refs.toaster.pop('success', 'Updated')
          })
          .catch(err => {
            this.$refs.toaster.pop('error', err.response.data.message ? err.response.data.message : 'Update failed')
          })
          .finally(() => {
            this.posting = false
          })
      }
    },
    mapCommunity(succ) {
      this.originallyConfidentialCommunity = succ.isConfidential
      this.community.owners = succ.owners
      this.tempAuthorizedPeople = succ.authorizedPeople
      this.community.authorizedPeople = this.tempAuthorizedPeople
      if (!succ.helpfulLinks) {
        succ.helpfulLinks = []
      }
      if (succ.helpfulLinks) {
        succ.helpfulLinks.forEach(x => {
          x.clientID = x.id
        })
      }
      if (succ.mocTypes) {
        succ.mocTypes.forEach(x => {
          x.clientID = x.id
        })
      }
      if (succ.changeCategories) {
        succ.changeCategories.forEach(x => {
          ;(x.approvalProcessTypes || []).forEach(y => {
            y.clientID = y.id
          })
          ;(x.mocTypes || []).forEach(y => {
            y.clientID = y.id
          })
          x.clientID = x.id
        })
      }
      if (succ.tasks) {
        succ.tasks.forEach(x => {
          x.originalClientId = x.clientID
          x.clientID = x.id
        })
      } else {
        succ.tasks = []
      }
      if (!succ.changeTypes) {
        succ.changeTypes = [
          {
            clientID: shortid.generate(),
            type: {
              key: ChangeTypes.TEMPORARY.toString(),
              value: ChangeTypes.properties[ChangeTypes.TEMPORARY].value
            },
            enabled: false,
            default_: false,
            tasks: []
          },
          {
            clientID: shortid.generate(),
            type: {
              key: ChangeTypes.PERMANENT.toString(),
              value: ChangeTypes.properties[ChangeTypes.PERMANENT].value
            },
            enabled: true,
            default_: true,
            tasks: []
          }
        ]
      } else {
        succ.changeTypes.forEach(x => {
          x.clientID = x.id
        })
      }

      this.community = succ
      this.community.durationInDays = succ.durationInDays || '365'
      if (!this.community.notificationEmails) {
        this.community.notificationEmails = {
          initials: getNotificationEmailsInitialState('initials').concat(getNotificationTagEmailsInitialState('initials')),
          reminders: getNotificationEmailsInitialState('reminders').concat(getNotificationTagEmailsInitialState('reminders'))
        }
      } else {
        Object.keys(this.community.notificationEmails)
          .filter(i => i !== 'id')
          .forEach(k => this.community.notificationEmails[k].forEach(c => (c.clientID = c.id)))
      }
      if (!this.community.qaRelationships) {
        this.community.qaRelationships = []
      } else {
        this.community.qaRelationships.forEach(r => {
          r.clientId = r.id
          r.criteria.forEach(c => {
            c.clientId = c.id
          })
        })
      }

      // combine names coming in with the master list to make sure the list is always up-to-date.

      for (let [key, value] of Object.entries(JSON.parse(JSON.stringify(i18next.getResourceBundle('en') || {})))) {
        if (this.displayNames.findIndex(x => x.key === key) === -1) {
          var tempData = JSON.stringify({
            key: key,
            defaultValue: value,
            value: ''
          })
          this.displayNames.push(JSON.parse(tempData))
        }
      }
      if (this.community.displayNames) {
        let existingNames = this.community.displayNames

        // do some data cleanup here
        for (var existingName of existingNames) {
          let name = this.displayNames.find(dn => dn.key === existingName.key)
          if (name) {
            // set our value
            name.value = existingName.value
          } else {
            // remove from our list.
            let index = this.community.displayNames.findIndex(x => x.key === existingName.key)
            if (index > -1) {
              this.community.displayNames.splice(index, 1)
            }
          }
        }
      }

      this.setGlobalApprovalProcessTypeList()
      this.setGlobalChangeCategoryMocTypeList()
      this.getQA()
      return this.community
    },
    getData: async function(shortName) {
      if (!this.gettingCommunity) {
        if (this.$route.params.shortName || shortName) {
          this.gettingCommunity = true
          await MOCAdminService.getCommunity(this.$route.params.shortName || shortName)
            .then(result => {
              this.mapCommunity(result)
            })
            .catch(err => {
              if (err.response) {
                switch (err.response.status) {
                  case 401:
                    this.$refs.toaster.pop('error', 'You are unauthorized to view this community')
                    break
                  default:
                    this.$refs.toaster.pop('error', 'Failed to retrieve data')
                }
              } else {
                this.$refs.toaster.pop('error', 'failed to load the data into the app')
              }
            })
            .finally(() => {
              this.gettingCommunity = false
            })
        }
      }
    },
    alert: function() {
      if (this.errorObject != null) {
        setTimeout(() => {
          this.errorObject = null
        }, 3000)
      }
      return this.errorObject != null
    },
    successAlert: function() {
      if (this.successMessage != null) {
        setTimeout(() => {
          this.successMessage = null
        }, 3000)
      }
      return this.successMessage != null
    },
    setUserOverrideNotificationsTo() {
      this.community.overrideNotificationsTo = this.$store.state.currentUser.user.userPrincipalName
    }
  },
  components: {
    AddOrEditMOCType,
    ChangeCategory,
    HelpfulLink,
    draggable: VueDraggableNext,
    Toaster,
    PeoplePickerMs,
    ApprovalProcessType,
    MOCType,
    Task,
    EmailTemplate,
    SimpleCollapsibleSection,
    ChangeTypeForm,
    QAForm,
    KVEdit,
    QAChooser,
    FAT,
    RelationshipTable,
    QATable,
    BulkUploadCsvFile,
    ChangeCategoryTable,
    ApprovalProcessTypeTable,
    ChangeCategoryMocTypeTable,
    TaskTable
  },
  created() {
    // edit mode if passed in the shortName param
    if (this.$route.params.shortName) {
      this.edit = true
      this.getData()
    }
  },
  provide() {
    return {
      getPeoplePickerInjectedProperties: this.getPeoplePickerInjectedProperties
    }
  }
}
</script>

<style scoped>
.relationship-table-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-show {
  height: 28px;
  background-color: #f5f5f5;
  font-size: .75rem;
  min-width: 50px;
  padding: 0 12.4444444444px;
}
</style>
<style>
.v-field__input {
  padding-left: 0;
}

.v-field--center-affix .v-label.v-field-label {
  margin: 0;
  font-size: 12px !important;
}
</style>
